// src/features/gamesReducer.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { envBool } from "../utils/misc";

const isDevMode: boolean = envBool(import.meta.env.VITE_ENV_DEV_MODE);


export type PermissionType = {
    permissionID: string;
}

export type PlayerType = { 
    /** players name */
    playerFirstName: string,
    playerLastName: string,
    /** players state id */
    UserStateID: number;
    /** date added in moment ms */
    dateAdded: number;
    /** date last online in moment ms */
    lastOnline: number;
    /** permission table */
    permissions: string[];
}

export type UtilityType = {
    /** utility name in db */
    id: string;
    /** amount of utility */
    amount: number;
    /** total available of utility */
    total: number;
    /** if they dont have it unlocked */
    isDisabled: boolean;
}

export type UpgradeType = {
    /** the id in db */
    id: string;
    /** the current level */
    level: number;
    /** maximum level */
    maxLevel: number;
    /** how much cost for next level */
    amount: number;
}

export type TransactionHistory = {
    /** players name */
    playerFirstName: string,
    playerLastName: string,
    /** players state id */
    UserStateID: number;
    /** fee amount */
    amountPaid: number;
    /** date of payment */
    paymentDate: number;
    /** status of payment */
    status: string;
}

export interface HardMoneyState {
    /** indentify house id */
    houseID: string;
    /** house owner state id */
    houseOwner: number;
    /** people that also have keys and their data */
    roommates: PlayerType[];
    /** all utility values */
    utilityValues: UtilityType[];
    /** all property upgrades */
    propertyUpgrades: UpgradeType[];
    /** all the property maintenance payments */
    propertyMaintenance: TransactionHistory[];
    /** commercial or residential */
    propertyType: string;
    /** location on map prob gonna use coords to location like hud */
    propertyLocation: string;
    /** cost of upkeep */
    propertyFee: number;
    /** next due date for cost of upkeep */
    feeDueDate: number;
    /** auto pay account */
    autoPayAccount: string;

    /** sets perms from client */
    permissions: {
        permID: string;
        permName: string;
    }[];

    /** deactive and activate alarm */
    isAlarmActive: boolean;

    /** the active house color */
    houseColorNumber: number;

    /** a list of  */
    propertyColors: {
        colorNumber: number;
        colorValue: string;
    }[];
}


const initialState: HardMoneyState = isDevMode ? {
    houseID: "MirrorPark1",
    houseOwner: 1,
    roommates: [
        {
            playerFirstName: "Rico",
            playerLastName: "Suave",
            UserStateID: 1,
            dateAdded: 1741551540000,
            lastOnline: 1741551540000,
            permissions: [
                "editperms",
                "deleteroommate",
            ],
        },
        {
            playerFirstName: "Richard",
            playerLastName: "Nixon",
            UserStateID: 126,
            dateAdded: 1742353814333,
            lastOnline: 1741966740000,
            permissions: [
                "editperms",
                "gaydetector",
            ],
        },
        {
            playerFirstName: "Adolf",
            playerLastName: "Handler",
            UserStateID: 15,
            dateAdded: 1741966740000,
            lastOnline: 1742353814333,
            permissions: [
                "deleteroommate",
                "editperms",
                "gaydetector",
            ],
        },
    ],
    utilityValues: [
        {
            id: "electric",
            amount: 7,
            total: 100,
            isDisabled: false,
        },
        {
            id: "water",
            amount: 23,
            total: 100,
            isDisabled: false,
        },
        {
            id: "temperature",
            amount: 21,
            total: 100,
            isDisabled: false,
        },
        {
            id: "heating",
            amount: 0,
            total: 100,
            isDisabled: true,
        },
        {
            id: "gas",
            amount: 0,
            total: 100,
            isDisabled: true,
        },
    ],
    propertyUpgrades: [
        {
            id: "storage",
            level: 1,
            maxLevel: 5,
            amount: 25000,
        },
        {
            id: "garage",
            level: 2,
            maxLevel: 5,
            amount: 100000,
        },
        {
            id: "security",
            level: 2,
            maxLevel: 5,
            amount: 54950,
        },
        {
            id: "keys",
            level: 3,
            maxLevel: 5,
            amount: 12900,
        },
    ],
    propertyMaintenance: [
        {
            playerFirstName: "Rico So Sexy Sexy Suave",
            playerLastName: "Suave",
            UserStateID: 1,
            amountPaid: 25950,
            paymentDate: 1742264700975,
            status: "PENDING",
        },
        {
            playerFirstName: "Rico",
            playerLastName: "Suave",
            UserStateID: 1,
            amountPaid: 23810,
            paymentDate: 1742083200000,
            status: "SUCCESS",
        },
    ],
    propertyType: "Residential",
    propertyLocation: "Roy Lowenstein Blvd",
    propertyFee: 25000,
    feeDueDate: 1742443200000,
    autoPayAccount: "1234",

    permissions: [
        {
            permID: "deleteroommate",
            permName: "Delete Roommate",
        },
        {
            permID: "editperms",
            permName: "Edit Permissions",
        },
        {
            permID: "gaydetector",
            permName: "Detect Gays",
        },
        {
            permID: "fixalarm",
            permName: "Alarm Setting",
        },
    ],

    isAlarmActive: false,

    houseColorNumber: 2,

    propertyColors: [
        { "colorNumber": 0, "colorValue": "rgba(240,240,240,1)" },
        { "colorNumber": 1, "colorValue": "rgba(211,211,211,1)" },
        { "colorNumber": 2, "colorValue": "rgba(51,51,51,1)" },
        { "colorNumber": 3, "colorValue": "rgba(225,123,38,1)" },
        { "colorNumber": 4, "colorValue": "rgba(216,191,216,1)" },
        { "colorNumber": 5, "colorValue": "rgba(173,216,230,1)" },
        { "colorNumber": 6, "colorValue": "rgba(168,194,161,1)" },
        { "colorNumber": 7, "colorValue": "rgba(102,188,70,1)" },
        { "colorNumber": 8, "colorValue": "rgba(234,195,155,1)" },
        { "colorNumber": 9, "colorValue": "rgba(255,192,203,1)" },
        { "colorNumber": 10, "colorValue": "rgba(236,36,182,1)" },
        { "colorNumber": 11, "colorValue": "rgba(235,167,98,1)" },
        { "colorNumber": 12, "colorValue": "rgba(237,32,36,1)" },
        { "colorNumber": 13, "colorValue": "rgba(77,61,107,1)" },
        { "colorNumber": 14, "colorValue": "rgba(108,126,178,1)" },
        { "colorNumber": 15, "colorValue": "rgba(32,86,209,1)" }
    ],
} : {
    houseID: "",
    houseOwner: 0,

    roommates: [],
    utilityValues: [],
    propertyUpgrades: [],
    propertyMaintenance: [],

    propertyType: "",
    propertyLocation: "",
    
    propertyFee: 0,
    feeDueDate: 0,
    autoPayAccount: "",

    permissions: [],

    isAlarmActive: false,

    houseColorNumber: 0,

    propertyColors: [],
};

const hardmoneyReducer = createSlice({
    name: "hardmoneyReducer",
    initialState,
    reducers: {
        /**
         * Sets The Current Minigame to Show
         * @object {object} currentMinigame - the object that holds the minigame data
         */
        SET_HOUSEDATA: (state, action: PayloadAction<HardMoneyState>) => {
            //console.log("SETTING LAB DATA", action.payload);
            return {...state, ...action.payload};
        },

        /** artifically set alarm active */
        DEV_SET_isAlarmActive: (state, action: PayloadAction<boolean>) => {
            state.isAlarmActive = action.payload;
        },

        /** artifically set pay account */
        DEV_SET_autoPayAccount: (state, action: PayloadAction<string>) => {
            //console.log("SETTING LAB DATA", action.payload);
            state.autoPayAccount = action.payload;
            //console.log(state.autoPayAccount);
        },

        /** artifically add new roomate */
        DEV_ADD_roommate: (state, action: PayloadAction<number>) => {
            //console.log("SETTING LAB DATA", action.payload);
            state.roommates.push( 
                {
                    playerFirstName: "ADDED",
                    playerLastName: "ROOMATE",
                    UserStateID: action.payload,
                    dateAdded: 1742353814333,
                    lastOnline: 1742353814333,
                    permissions: [],
                },
            );
            //console.log(state.roommates);
        },

        /** artifically add new roomate */
        DEV_DELETE_roommate: (state, action: PayloadAction<number>) => {
            state.roommates = state.roommates.filter(roommate => roommate.UserStateID !== action.payload);
        },

        
        /** artifically set player perms */
        DEV_SET_roommate_perms: (state, action: PayloadAction<{ roommateSID: number; permissions: string[] }>) => {
            const { roommateSID, permissions } = action.payload;
            const roommateIndex = state.roommates.findIndex(roommate => roommate.UserStateID === roommateSID);
        
            // Fix condition to check if roommateIndex is >= 0
            if (roommateIndex >= 0) {
                state.roommates[roommateIndex] = {
                    ...state.roommates[roommateIndex],
                    permissions: [...permissions] 
                };
            }
        },

        /** artifically set player perms */
        DEV_SET_property_upgrades: (state, action: PayloadAction<{ upgradeID: string }>) => {
            const { upgradeID } = action.payload;
            const upgradeIndex = state.propertyUpgrades.findIndex(upgrade => upgrade.id === upgradeID);
        
            // Fix condition to check if roommateIndex is >= 0
            if (upgradeIndex >= 0) {
                const upgrade = state.propertyUpgrades[upgradeIndex];
                state.propertyUpgrades[upgradeIndex] = {
                    ...upgrade,
                    level: upgrade.level + 1,
                };
            }
        },

        /** artifically set house color */
        DEV_SET_houseColorNumber: (state, action: PayloadAction<number>) => {
            state.houseColorNumber = action.payload;
        },

    },
});

export const { SET_HOUSEDATA, DEV_SET_autoPayAccount, DEV_ADD_roommate, DEV_DELETE_roommate, DEV_SET_roommate_perms, DEV_SET_property_upgrades, DEV_SET_isAlarmActive, DEV_SET_houseColorNumber } = hardmoneyReducer.actions;
export default hardmoneyReducer.reducer;
